import { router } from '@inertiajs/react'
import { Crown, RotateCcw } from 'lucide-react'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Label } from '@/components/ui/Label'
import { Input } from '@/components/ui/Input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/RadixSelect'
import { godModeAccounts } from '@/routeHelpers'
import type { GodModeSubscription, GodModePlan } from '@/types/godmode'

interface SubscriptionTabProps {
  accountSlug: string
  subscription: GodModeSubscription | null
  plans: GodModePlan[]
}

export function SubscriptionTab({ accountSlug, subscription, plans }: SubscriptionTabProps) {
  const handlePlanChange = (planId: string) => {
    router.patch(godModeAccounts.update.path({ id: accountSlug }), {
      plan_id: planId,
    })
  }

  const handleEntitlementUpdate = (key: string, value: string) => {
    const parsedValue = value === 'true' ? true : value === 'false' ? false : parseInt(value, 10)
    router.patch(godModeAccounts.updateEntitlement.path({ id: accountSlug }), {
      entitlement_key: key,
      value: parsedValue,
    })
  }

  const handleResetEntitlement = (key: string) => {
    router.delete(godModeAccounts.resetEntitlement.path({ id: accountSlug }), {
      data: { entitlement_key: key },
    })
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Crown className="size-5 text-amber-500" />
          Subscription
        </CardTitle>
        <CardDescription>
          Manage this account's plan and entitlements
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <Label>Plan</Label>
          <Select
            value={subscription?.plan_id || ''}
            onValueChange={handlePlanChange}
          >
            <SelectTrigger className="w-full cursor-pointer">
              <SelectValue placeholder="Select a plan" />
            </SelectTrigger>
            <SelectContent>
              {plans.map((plan) => (
                <SelectItem key={plan.id} value={plan.id} className="cursor-pointer">
                  {plan.name}
                  {plan.internal && ' (Internal)'}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {subscription && (
            <p className="text-xs text-muted-foreground">
              Status: {subscription.status} | Period ends: {subscription.current_period_end ? new Date(subscription.current_period_end).toLocaleDateString() : 'N/A'}
            </p>
          )}
        </div>

        {subscription && (
          <div className="space-y-4">
            <div className="border-t pt-4">
              <h4 className="font-medium mb-4">Entitlements</h4>
              <div className="space-y-4">
                {Object.entries(subscription.entitlements).map(([key, ent]) => (
                  <div key={key} className="flex items-center gap-4">
                    <div className="flex-1">
                      <Label className="flex items-center gap-2">
                        {ent.name}
                        {ent.overridden && (
                          <span className="text-xs bg-amber-500/20 text-amber-500 px-1.5 py-0.5 rounded">
                            Overridden
                          </span>
                        )}
                      </Label>
                      <p className="text-xs text-muted-foreground">{key}</p>
                    </div>
                    <div className="w-32">
                      {ent.value_type === 'boolean' ? (
                        <Select
                          value={String(ent.value)}
                          onValueChange={(v: string) => handleEntitlementUpdate(key, v)}
                        >
                          <SelectTrigger className="cursor-pointer">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="true" className="cursor-pointer">Yes</SelectItem>
                            <SelectItem value="false" className="cursor-pointer">No</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input
                          type="number"
                          value={ent.value as number}
                          onChange={(e) => handleEntitlementUpdate(key, e.target.value)}
                          className="w-full"
                        />
                      )}
                    </div>
                    {ent.overridden && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleResetEntitlement(key)}
                        className="cursor-pointer"
                        title="Reset to plan default"
                      >
                        <RotateCcw className="size-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {Object.keys(subscription.usage).length > 0 && (
              <div className="border-t pt-4">
                <h4 className="font-medium mb-4">Usage This Period</h4>
                <div className="grid grid-cols-2 gap-4">
                  {Object.entries(subscription.usage).map(([key, value]) => (
                    <div key={key} className="text-sm">
                      <span className="text-muted-foreground">{key}:</span>{' '}
                      <span className="font-medium">{value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {!subscription && (
          <p className="text-muted-foreground">
            No subscription found. Select a plan to create one.
          </p>
        )}
      </CardContent>
    </Card>
  )
}
